import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { FullPageLoader } from '@components';
import UserAuth from '../services/auth';
import { RootState } from '../stores/store';
import { NAV_ROUTES } from '../constants/navRoutes';
import useNavigateToLogin from '../hooks/useNavigateToLogin';
import useUserDetails from '../hooks/api/useUserDetails';
import { USER_APPROVAL_STATUS } from '../constants/constants';

const ACCESS_ROUTE_BY_STATUS: Record<string, string> = {
  [USER_APPROVAL_STATUS.PENDING_OWNER_APPROVAL]: NAV_ROUTES.ACCESS_PENDING,
  [USER_APPROVAL_STATUS.USER_PENDING_WORKSPACE_SETUP]: NAV_ROUTES.ACCESS_COMPANY_PENDING,
  [USER_APPROVAL_STATUS.COMPANY_CREATION_REQUESTED]: NAV_ROUTES.ACCESS_COMPANY_PENDING,
  [USER_APPROVAL_STATUS.REJECTED]: NAV_ROUTES.ACCESS_REJECTED,
};

const AccessRequestLayout: FC = () => {
  const { navigateToLogin } = useNavigateToLogin();
  const { getUserDetails } = useUserDetails();
  const navigate = useNavigate();
  const location = useLocation();
  const token =
    useSelector((state: RootState) => state?.AppData?.auth?.access_token) ||
    UserAuth.accessToken ||
    '';
  const accessRequest = useSelector((state: RootState) => state?.UserAccessData?.accessRequest);
  const [isLoading, setIsLoading] = useState(!accessRequest);

  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    if (accessRequest) {
      const expectedRoute = ACCESS_ROUTE_BY_STATUS[accessRequest.status];
      if (expectedRoute && location.pathname !== expectedRoute) {
        navigate(expectedRoute, { replace: true });
      }
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const fetchUserDetails = async () => {
      try {
        const response = await getUserDetails();
        if (cancelled) return;

        if (!response?.accessStatus) {
          navigate(response ? NAV_ROUTES.DASHBOARD : NAV_ROUTES.PAGE_NOT_FOUND, {
            replace: true,
          });
        }
      } catch {
        if (cancelled) return;
        navigate(NAV_ROUTES.PAGE_NOT_FOUND, { replace: true });
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchUserDetails();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getUserDetails identity is unstable
  }, [token, accessRequest, location.pathname, navigate]);

  if (!token) {
    navigateToLogin();
    return null;
  }

  if (isLoading) {
    return <FullPageLoader />;
  }

  if (!accessRequest) {
    return <Navigate to={NAV_ROUTES.PAGE_NOT_FOUND} replace />;
  }

  return <Outlet />;
};

export default AccessRequestLayout;
